import type { AxisLabelsFormatterCallbackFunction, XAxisOptions } from "highcharts"
import { useMemo } from "react"
import type { GranularityType } from "../Chart"
import { chartCSSVars } from "../chartSettingColors"
import { CROSSHAIR_COLOR, CROSSHAIR_Z_INDEX } from "../utils/plotOverlays"

const DEFAULT_X_AXIS_OPTIONS: XAxisOptions = {
  type: "category",
  labels: {
    rotation: -45,
  },
}

const DEFAULT_TIMEBASED_X_AXIS_OPTIONS: XAxisOptions = {
  type: "datetime",
  dateTimeLabelFormats: {
    day: "%m/%d/%y",
    week: "%m/%d/%y",
    month: "%b '%y",
  },
  units: [
    [
      "millisecond", // unit name
      [1, 2, 5, 10, 20, 25, 50, 100, 200, 500], // allowed multiples
    ],
    ["second", [1, 2, 5, 10, 15, 30]],
    ["minute", [1, 2, 5, 10, 15, 30]],
    ["hour", [1, 2, 3, 4, 6, 8, 12]],
    ["day", [1, 2, 3, 4]],
    ["week", [1, 2]],
    ["month", [1, 2, 3, 4, 6]],
    ["year", null],
  ],
}

const axisFormatter: AxisLabelsFormatterCallbackFunction = function axisFormatter() {
  switch (this.axis.series[0].options.custom!.granularity) {
    case "quarter": {
      const parsedDate = new Date(this.value)
      const quarter = Math.floor((parsedDate.getUTCMonth() + 3) / 3)

      return `Q${quarter} '${parsedDate.getUTCFullYear().toString().substring(2)}`
    }
    case "month": {
      return this.axis.chart.time.dateFormat(`%b '%y`, this.value as number)
    }
    case "week": {
      return this.axis.chart.time.dateFormat("%m/%d/%y", this.value as number)
    }
    default: {
      return this.axis.chart.time.dateFormat(this.dateTimeLabelFormat!, this.value as number)
    }
  }
}

/** @deprecated */
export function useXAxisOptions(
  isTimeBased: boolean,
  _granularity: GranularityType,
  xAxisOptions?: XAxisOptions,
  isBarChart?: boolean,
  reverseBarOrder = false,
): XAxisOptions {
  return useMemo<XAxisOptions>(() => {
    const timeBasedOptions = isTimeBased ? DEFAULT_TIMEBASED_X_AXIS_OPTIONS : DEFAULT_X_AXIS_OPTIONS

    const xAxis: Partial<XAxisOptions> = {
      id: "main-xaxis",
      overscroll: 0,
      height: isBarChart ? "95%" : undefined,
      gridLineColor: chartCSSVars.axis.gridColor,
      lineColor: chartCSSVars.axis.gridColor,
      tickColor: chartCSSVars.axis.tickColor,
      // Mirrors the y-axis crosshair in `yAxes.ts`, same dash and colour.
      // Without this the vertical hover line falls back to Highcharts' own
      // default — solid, and a hardcoded #cccccc that ignores the theme, so it
      // read as a pale line on the dark surface and didn't match the dotted
      // horizontal line it crosses. Declared before the spreads so a caller
      // passing its own `crosshair` still wins.
      crosshair: {
        dashStyle: "ShortDot",
        color: CROSSHAIR_COLOR,
        zIndex: CROSSHAIR_Z_INDEX,
      },
      ...timeBasedOptions,
      ...xAxisOptions,
      showFirstLabel: true,
      showLastLabel: true,
      reversedStacks: reverseBarOrder,
      labels: {
        overflow: "justify",
        formatter: isTimeBased ? axisFormatter : undefined,
        ...timeBasedOptions.labels,
        ...xAxisOptions?.labels,
        style: {
          fontSize: chartCSSVars.axis.fontSize,
          color: chartCSSVars.axis.color,
          fontWeight: "400",
          ...timeBasedOptions.labels?.style,
          ...xAxisOptions?.labels?.style,
        },
        distance: 0,
      },
    }

    return xAxis
  }, [isTimeBased, xAxisOptions, isBarChart, reverseBarOrder])
}
